import { useRef } from "react";
import QRCode from "react-qr-code";
import { jsPDF } from "jspdf";
import { Download, FileText, Copy, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const svgToPng = (container, size = 1024, pad = 48) =>
  new Promise((resolve, reject) => {
    const svg = container?.querySelector("svg");
    if (!svg) return reject(new Error("QR no encontrado"));
    const blob = new Blob([new XMLSerializer().serializeToString(svg)], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = size; c.height = size;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, pad, pad, size - pad * 2, size - pad * 2);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = url;
  });

export default function MerchantCouponQR({ merchant }) {
  const ref = useRef(null);
  const url = `${window.location.origin}/c/${merchant.merchant_id}`;
  const safe = (merchant.business_name || "comercio").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);

  const downloadPNG = async () => {
    try {
      const data = await svgToPng(ref.current);
      const a = document.createElement("a");
      a.download = `qr-cupones-${safe}.png`; a.href = data; a.click();
    } catch { toast.error("No se pudo generar el PNG"); }
  };

  const downloadPDF = async () => {
    try {
      const qr = await svgToPng(ref.current, 1024, 24);
      const pdf = new jsPDF({ unit: "mm", format: "a4" });
      const W = 210, H = 297;
      pdf.setFillColor(10, 10, 10); pdf.rect(0, 0, W, H, "F");
      pdf.setFillColor(255, 0, 122); pdf.roundedRect(14, 14, W - 28, 40, 8, 8, "F");
      pdf.setTextColor(255, 255, 255); pdf.setFont("helvetica", "bold"); pdf.setFontSize(34);
      pdf.text("CUPONES EXCLUSIVOS", W / 2, 33, { align: "center" });
      pdf.setFontSize(13); pdf.setFont("helvetica", "normal");
      pdf.text("Escanea y recibe descuentos directo en tu celular", W / 2, 45, { align: "center" });
      pdf.setFontSize(26); pdf.setFont("helvetica", "bold");
      pdf.text(pdf.splitTextToSize(merchant.business_name || "", W - 50), W / 2, 74, { align: "center" });
      const qs = 118, qx = (W - qs) / 2, qy = 88;
      pdf.setFillColor(255, 255, 255); pdf.roundedRect(qx - 8, qy - 8, qs + 16, qs + 16, 8, 8, "F");
      pdf.addImage(qr, "PNG", qx, qy, qs, qs);
      pdf.setTextColor(255, 255, 255); pdf.setFontSize(16);
      pdf.text("1. Apunta tu cámara al código", W / 2, qy + qs + 22, { align: "center" });
      pdf.text("2. Guarda tu CUPONERA en tu pantalla de inicio", W / 2, qy + qs + 31, { align: "center" });
      pdf.text("3. Recibe cupones y ahorra cada vez que vengas", W / 2, qy + qs + 40, { align: "center" });
      pdf.setFillColor(0, 85, 255); pdf.roundedRect(40, qy + qs + 50, W - 80, 14, 7, 7, "F");
      pdf.setFontSize(12); pdf.text("Sin descargar apps · Sin registro · Gratis", W / 2, qy + qs + 59, { align: "center" });
      pdf.setTextColor(150, 150, 150); pdf.setFontSize(8); pdf.setFont("helvetica", "normal");
      pdf.text(url, W / 2, H - 18, { align: "center" });
      pdf.text("Powered by HOLA RADAR", W / 2, H - 12, { align: "center" });
      pdf.save(`qr-cupones-${safe}.pdf`);
      toast.success("PDF listo para imprimir");
    } catch { toast.error("No se pudo generar el PDF"); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Enlace copiado"); } catch { toast.error("No se pudo copiar"); }
  };

  return (
    <div className="rounded-3xl bg-[#0A0A0A] text-white p-6 grid md:grid-cols-[auto_1fr] gap-6 items-center" data-testid="coupon-qr-section">
      <div ref={ref} className="bg-white rounded-2xl p-4 mx-auto w-fit">
        <QRCode value={url} size={180} bgColor="#FFFFFF" fgColor="#0A0A0A" level="H" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF007A] flex items-center gap-1"><QrCode className="w-3 h-3" /> Tu QR personalizado</div>
        <h2 className="font-heading font-extrabold text-2xl mt-1 leading-tight">Cada escaneo = un cliente en TU base de datos</h2>
        <p className="text-sm text-white/70 mt-2 leading-relaxed">
          Este QR es <strong className="text-white">exclusivo de {merchant.business_name}</strong>. Quien lo escanea queda en tu lista, recibe tus cupones de bienvenida y también activa su radar GPS de ofertas.
          Pégalo en la caja, la mesa, el espejo, la bolsa de compra…
        </p>
        <div className="mt-3 flex gap-2 items-center">
          <code className="text-[11px] bg-white/10 rounded-lg px-2 py-1.5 flex-1 truncate" data-testid="coupon-qr-url">{url}</code>
          <Button onClick={copy} size="sm" variant="outline" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10" data-testid="coupon-qr-copy"><Copy className="w-3.5 h-3.5" /></Button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={downloadPDF} className="rounded-full bg-[#FF007A] hover:bg-[#e6006e] font-bold" data-testid="coupon-qr-pdf"><FileText className="w-4 h-4" /> Póster PDF</Button>
          <Button onClick={downloadPNG} className="rounded-full bg-white text-black hover:bg-neutral-200 font-bold" data-testid="coupon-qr-png"><Download className="w-4 h-4" /> PNG</Button>
        </div>
      </div>
    </div>
  );
}
